/**
 * VerticalTimeline
 *
 * Animated vertical timeline showing transits with planet symbols,
 * aspects, and explanations.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TimelineHotspot } from '../../types';
import './VerticalTimeline.css';

// Planet symbols with Unicode
const PLANET_SYMBOLS: Record<string, string> = {
  'Sun': '☉',
  'Moon': '☽',
  'Mercury': '☿',
  'Venus': '♀',
  'Mars': '♂',
  'Jupiter': '♃',
  'Saturn': '♄',
  'Uranus': '♅',
  'Neptune': '♆',
  'Pluto': '♇',
  'North Node': '☊',
  'South Node': '☋',
  'Chiron': '⚷',
  'Ascendant': 'ASC',
  'Midheaven': 'MC',
  'IC': 'IC',
  'Descendant': 'DSC',
};

// Aspect symbols
const ASPECT_SYMBOLS: Record<string, { symbol: string; color: string }> = {
  'conjunction': { symbol: '☌', color: '#ffd700' },
  'opposition': { symbol: '☍', color: '#f87171' },
  'trine': { symbol: '△', color: '#4ade80' },
  'square': { symbol: '□', color: '#f87171' },
  'sextile': { symbol: '⚹', color: '#4ade80' },
  'quincunx': { symbol: '⚻', color: '#fbbf24' },
};


interface VerticalTimelineProps {
  hotspots: TimelineHotspot[];
  year: number;
  onAskLucy: (hotspot: TimelineHotspot) => void;
}

export function VerticalTimeline({ hotspots, year, onAskLucy }: VerticalTimelineProps) {
  const { t, i18n } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Group hotspots by month
  const hotspotsByMonth = hotspots.reduce((acc, hotspot) => {
    const month = new Date(hotspot.date).getMonth();
    if (!acc[month]) acc[month] = [];
    acc[month].push(hotspot);
    return acc;
  }, {} as Record<number, TimelineHotspot[]>);

  const monthNames = t('timeline.months', { returnObjects: true }) as string[];

  // Scroll to current month on load
  useEffect(() => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    if (year === currentYear && timelineRef.current) {
      const monthElement = timelineRef.current.querySelector(`[data-month="${currentMonth}"]`);
      if (monthElement) {
        monthElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [year]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'sr' ? 'sr-Latn' : 'en-US', {
      day: 'numeric',
      month: 'long',
    });
  };

  const getTypeClass = (type: string) => {
    switch (type) {
      case 'favorable': return 'type-favorable';
      case 'challenging': return 'type-challenging';
      default: return 'type-neutral';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'favorable': return t('timeline.favorable');
      case 'challenging': return t('timeline.challenging');
      default: return t('timeline.neutral');
    }
  };

  return (
    <div className="vertical-timeline" ref={timelineRef}>
      {/* Timeline line */}
      <div className="timeline-line" />

      {/* Today marker */}
      {year === new Date().getFullYear() && (
        <div
          className="today-marker"
          style={{
            top: `${(new Date().getMonth() / 12) * 100}%`
          }}
        >
          <span className="today-label">{t('timeline.today') || 'Today'}</span>
        </div>
      )}

      {/* Months */}
      {Array.from({ length: 12 }, (_, monthIndex) => (
        <div
          key={monthIndex}
          className="timeline-month"
          data-month={monthIndex}
        >
          <div className="month-header">
            <span className="month-name">{monthNames[monthIndex]}</span>
            <span className="month-count">
              {hotspotsByMonth[monthIndex]?.length || 0} {t('timeline.transits') || 'transits'}
            </span>
          </div>

          {/* Hotspots for this month */}
          <div className="month-hotspots">
            {(hotspotsByMonth[monthIndex] || []).map((hotspot, index) => (
              <TransitCard
                key={hotspot.id}
                hotspot={hotspot}
                isExpanded={expandedId === hotspot.id}
                onToggle={() => setExpandedId(expandedId === hotspot.id ? null : hotspot.id)}
                onAskLucy={() => onAskLucy(hotspot)}
                formatDate={formatDate}
                getTypeClass={getTypeClass}
                getTypeLabel={getTypeLabel}
                animationDelay={index * 0.1}
                lang={i18n.language}
              />
            ))}

            {/* Empty month placeholder */}
            {!hotspotsByMonth[monthIndex]?.length && (
              <div className="empty-month">
                <span className="empty-icon">✧</span>
                <span className="empty-text">{t('timeline.noTransits') || 'No major transits'}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

interface TransitCardProps {
  hotspot: TimelineHotspot;
  isExpanded: boolean;
  onToggle: () => void;
  onAskLucy: () => void;
  formatDate: (date: string) => string;
  getTypeClass: (type: string) => string;
  getTypeLabel: (type: string) => string;
  animationDelay: number;
  lang: string;
}

function TransitCard({
  hotspot,
  isExpanded,
  onToggle,
  onAskLucy,
  formatDate,
  getTypeClass,
  getTypeLabel,
  animationDelay,
  lang,
}: TransitCardProps) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const transit = hotspot.transit;
  const aspectInfo = ASPECT_SYMBOLS[transit.aspect.toLowerCase()] || { symbol: '●', color: '#888' };
  const planetSymbol = PLANET_SYMBOLS[transit.planet] || transit.planet.charAt(0);
  const natalSymbol = PLANET_SYMBOLS[transit.natal_point] || transit.natal_point.charAt(0);

  return (
    <div
      ref={cardRef}
      className={`transit-card ${getTypeClass(hotspot.type)} ${isExpanded ? 'expanded' : ''} ${isVisible ? 'visible' : ''}`}
      style={{ animationDelay: `${animationDelay}s` }}
      onClick={onToggle}
    >
      {/* Timeline dot */}
      <div className="card-dot">
        <div className={`dot-inner ${getTypeClass(hotspot.type)}`} />
      </div>

      {/* Card content */}
      <div className="card-content">
        {/* Header with planets and aspect */}
        <div className="card-header">
          <div className="aspect-visual">
            {/* Transiting planet */}
            <div className={`planet transiting ${isVisible ? 'animate' : ''}`}>
              <span className="planet-symbol">{planetSymbol}</span>
              <span className="planet-name">{transit.planet}</span>
            </div>

            {/* Aspect symbol */}
            <div
              className="aspect-symbol"
              style={{ color: aspectInfo.color }}
            >
              {aspectInfo.symbol}
            </div>

            {/* Natal point */}
            <div className={`planet natal ${isVisible ? 'animate' : ''}`}>
              <span className="planet-symbol">{natalSymbol}</span>
              <span className="planet-name">{transit.natal_point}</span>
            </div>
          </div>

          {/* Type badge and date */}
          <div className="card-meta">
            <span className={`type-badge ${getTypeClass(hotspot.type)}`}>
              {getTypeLabel(hotspot.type)}
            </span>
            <span className="card-date">{formatDate(hotspot.date)}</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="card-title">
          {hotspot.title[lang as 'en' | 'sr'] || hotspot.title.en}
        </h3>

        {/* Brief description (always visible) */}
        <p className="card-brief">
          {hotspot.brief[lang as 'en' | 'sr'] || hotspot.brief.en}
        </p>

        {/* Expanded content */}
        {isExpanded && (
          <div className="card-expanded">
            {/* Orb info */}
            <div className="orb-info">
              <span className="orb-label">{t('timeline.orb')}:</span>
              <span className="orb-value">{transit.orb.toFixed(1)}°</span>
            </div>

            {/* Life areas activated */}
            {hotspot.life_areas && hotspot.life_areas.length > 0 && (
              <div className="life-areas-info">
                <span className="life-areas-label">{t('timeline.lifeAreas')}:</span>
                <div className="life-areas-list">
                  {hotspot.life_areas.map((area) => (
                    <div key={area.house} className="life-area-item">
                      <span className="house-number">{area.house}.</span>
                      <span className="house-theme">
                        {area.theme[lang as 'en' | 'sr'] || area.theme.en}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Houses affected (fallback) */}
            {(!hotspot.life_areas || hotspot.life_areas.length === 0) && hotspot.houses_affected?.length > 0 && (
              <div className="houses-info">
                <span className="houses-label">{t('timeline.housesAffected')}:</span>
                <div className="houses-list">
                  {hotspot.houses_affected.map((house) => (
                    <span key={house} className="house-badge">{house}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Aspect explanation */}
            <div className="aspect-explanation">
              <AspectExplanation
                aspect={transit.aspect}
                planet={transit.planet}
                natalPoint={transit.natal_point}
                lang={lang}
              />
            </div>

            {/* Ask Lucy button */}
            <button
              className="ask-lucy-btn"
              onClick={(e) => {
                e.stopPropagation();
                onAskLucy();
              }}
            >
              {t('timeline.askLucy')}
            </button>
          </div>
        )}

        {/* Expand indicator */}
        <div className="expand-indicator">
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>
    </div>
  );
}

interface AspectExplanationProps {
  aspect: string;
  planet: string;
  natalPoint: string;
  lang: string;
}

function AspectExplanation({ aspect, planet, natalPoint, lang }: AspectExplanationProps) {
  // Generate contextual explanation based on aspect type
  const explanations: Record<string, Record<string, string>> = {
    conjunction: {
      en: `${planet} merges its energy with your natal ${natalPoint}, intensifying and focusing this area of life. This is a powerful moment of new beginnings and heightened awareness.`,
      sr: `${planet} spaja svoju energiju sa vašim natalnim ${natalPoint}, intenzivira i fokusira ovu oblast života. Ovo je moćan trenutak novih početaka i pojačane svesnosti.`,
    },
    opposition: {
      en: `${planet} sits across from your natal ${natalPoint}, creating tension that demands balance. Others may mirror back what you need to see. Integration is key.`,
      sr: `${planet} se nalazi naspram vašeg natalnog ${natalPoint}, stvarajući tenziju koja zahteva ravnotežu. Drugi vam mogu odražavati ono što treba da vidite.`,
    },
    trine: {
      en: `${planet} flows harmoniously with your natal ${natalPoint}, bringing ease and natural opportunities. Talents emerge effortlessly during this period.`,
      sr: `${planet} teče harmonično sa vašim natalnim ${natalPoint}, donoseći lakoću i prirodne prilike. Talenti se pojavljuju bez napora u ovom periodu.`,
    },
    square: {
      en: `${planet} challenges your natal ${natalPoint}, creating friction that motivates change. This tension, while uncomfortable, drives growth and accomplishment.`,
      sr: `${planet} izaziva vaš natalni ${natalPoint}, stvarajući trenje koje motiviše promenu. Ova tenzija, iako neugodna, pokreće rast i postignuće.`,
    },
    sextile: {
      en: `${planet} offers supportive energy to your natal ${natalPoint}. Opportunities arise through initiative - they won't fall in your lap but are easily accessible.`,
      sr: `${planet} nudi podržavajuću energiju vašem natalnom ${natalPoint}. Prilike nastaju kroz inicijativu - neće vam pasti u krilo, ali su lako dostupne.`,
    },
    quincunx: {
      en: `${planet} forms an awkward angle to your natal ${natalPoint}, requiring adjustments. Something must be released or modified for integration to occur.`,
      sr: `${planet} formira nezgodan ugao prema vašem natalnom ${natalPoint}, zahtevajući prilagođavanja. Nešto mora biti pušteno ili modifikovano da bi došlo do integracije.`,
    },
  };

  const aspectKey = aspect.toLowerCase();
  const text = explanations[aspectKey]?.[lang] || explanations[aspectKey]?.en || '';

  return <p className="explanation-text">{text}</p>;
}

export default VerticalTimeline;
